import { useEffect, useMemo, useState } from "react";

import { useGetAllowedSitesApi } from "../../../api";
import { useSelectedSites } from "../../../hooks/useSelectedSites";
import { SiteWithModuleStatusDto } from "../../../models/site-with-module-status";
import Button from "../../Button/Button";
import Dropdown from "../../Dropdown/Dropdown";
import CompactContent from "./CompactContent";
import { COMPACT_MAX, MODULE } from "./constants";
import GridContent from "./GridContent";
import styles from "./SiteSelector.module.css";
import { MultiSiteUrlResolver, SiteUrlResolver } from "./types";

const EMPTY_SITES: SiteWithModuleStatusDto[] = [];
const PRODUCTION_AREA_TAB_ORDER = ["Grinding", "Hoisting", "HPR"];

type Props = {
  getSiteUrl?: SiteUrlResolver;
  /** When false, the multi-site affordance is hidden. Defaults to true. */
  allowMultiSite?: boolean;
  getMultiSiteUrl?: MultiSiteUrlResolver;
};

function SiteSelector({
  getSiteUrl,
  getMultiSiteUrl,
  allowMultiSite = true,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeProductionArea, setActiveProductionArea] = useState<string>();

  const { data: mySites, isLoading } = useGetAllowedSitesApi({
    includeRootEquipments: true,
    includeSiteDetails: MODULE === "Condition Monitoring",
  });
  const sites = mySites?.sites ?? EMPTY_SITES;
  const { selectedSites, selectedSite } = useSelectedSites();

  const productionAreaTabs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const site of sites) {
      if (!site.productionArea) continue;
      counts.set(
        site.productionArea,
        (counts.get(site.productionArea) ?? 0) + 1
      );
    }

    return Array.from(counts, ([productionArea, siteCount]) => ({
      productionArea,
      siteCount,
    })).sort((a, b) => {
      const aIndex = PRODUCTION_AREA_TAB_ORDER.indexOf(a.productionArea);
      const bIndex = PRODUCTION_AREA_TAB_ORDER.indexOf(b.productionArea);

      if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
      if (aIndex >= 0) return -1;
      if (bIndex >= 0) return 1;

      return a.productionArea.localeCompare(b.productionArea);
    });
  }, [sites]);

  useEffect(() => {
    if (productionAreaTabs.length === 0) {
      setActiveProductionArea(undefined);
      return;
    }

    setActiveProductionArea((current) => {
      if (
        current &&
        productionAreaTabs.some((tab) => tab.productionArea === current)
      ) {
        return current;
      }

      if (
        mySites?.defaultProductionArea &&
        productionAreaTabs.some(
          (tab) => tab.productionArea === mySites.defaultProductionArea
        )
      ) {
        return mySites.defaultProductionArea;
      }

      return productionAreaTabs[0].productionArea;
    });
  }, [mySites?.defaultProductionArea, productionAreaTabs]);

  const visibleSites =
    productionAreaTabs.length > 1 && activeProductionArea
      ? sites.filter((site) => site.productionArea === activeProductionArea)
      : sites;

  const buttonText =
    selectedSites.length > 2
      ? `${selectedSites.length} sites`
      : selectedSites.length === 2
        ? selectedSites.join(", ")
        : (selectedSite ?? "Select site");

  if (isLoading || sites.length === 1) {
    return <p className={styles["site-label"]}>{buttonText}</p>;
  }

  const isCompact = sites.length <= COMPACT_MAX;

  return (
    <Dropdown
      anchor="right"
      variant="no-padding"
      dropdown={
        isCompact ? (
          <CompactContent
            site={selectedSite}
            sites={visibleSites}
            productionAreaTabs={productionAreaTabs}
            activeProductionArea={activeProductionArea}
            setActiveProductionArea={setActiveProductionArea}
            setIsOpen={setIsOpen}
            getSiteUrl={getSiteUrl}
            allowMultiSite={allowMultiSite}
            getMultiSiteUrl={getMultiSiteUrl}
          />
        ) : (
          <GridContent
            site={selectedSite}
            sites={visibleSites}
            productionAreaTabs={productionAreaTabs}
            activeProductionArea={activeProductionArea}
            setActiveProductionArea={setActiveProductionArea}
            setIsOpen={setIsOpen}
            getSiteUrl={getSiteUrl}
            allowMultiSite={allowMultiSite}
            getMultiSiteUrl={getMultiSiteUrl}
          />
        )
      }
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <Button
        toggled={isOpen}
        onClick={() => setIsOpen((o) => !o)}
        icon="Caret_down"
        text={buttonText}
        iconPosition="right"
        size="small"
        variant="transparent"
      />
    </Dropdown>
  );
}

export default SiteSelector;
